import { useLiveQuery } from 'dexie-react-hooks';
import { oldestPendingOp, type SyncRejectionRow } from '@pos/local-db';
import { db } from '../../lib/db.ts';
import { env } from '../../lib/env.ts';
import { supabase } from '../../lib/supabase.ts';

export interface SyncConflict {
  id: string;
  table_name: string;
  row_id: string;
  field: string;
  base_value: unknown;
  server_value: unknown;
  incoming_value: unknown;
  /** applied: quedó lo que llegó tarde (era más reciente). kept: quedó lo que ya estaba. */
  outcome: 'applied' | 'kept';
  server_changed_at: string | null;
  server_changed_by: string | null;
  server_device_id: string | null;
  op_created_at: string;
  user_id: string | null;
  device_id: string | null;
  created_at: string;
}

export interface DeviceHealth {
  device_id: string;
  app_version: string | null;
  pending_ops: number;
  rejected_ops: number;
  oldest_pending_at: string | null;
  last_sync_at: string | null;
}

/** Datos que quedaron distintos por cambios en dos dispositivos (solo dueño, online). */
export async function fetchConflicts(businessId: string): Promise<SyncConflict[]> {
  const { data, error } = await supabase
    .from('sync_conflicts')
    .select('*')
    .eq('business_id', businessId)
    .eq('status', 'open')
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  return (data ?? []) as SyncConflict[];
}

export async function fetchDeviceHealth(businessId: string): Promise<DeviceHealth[]> {
  const { data, error } = await supabase.from('device_health').select('*').eq('business_id', businessId);
  if (error) throw new Error(error.message);
  return (data ?? []) as DeviceHealth[];
}

/** 'keep': dejarlo así. 'revert': poner el otro valor. */
export async function resolveConflict(id: string, action: 'keep' | 'revert'): Promise<void> {
  const { error } = await supabase.rpc('resolve_sync_conflict', { p_id: id, p_action: action });
  if (error) throw new Error(error.message);
}

/** El dueño aplica un cambio de la cuarentena con sus permisos. Devuelve el motivo si se volvió a rechazar. */
export async function applyRejection(id: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('apply_sync_rejection', { p_id: id });
  if (error) throw new Error(error.message);
  const result = data as { status: string; error?: string } | null;
  return result?.status === 'ok' ? null : (result?.error ?? 'El servidor no lo aceptó.');
}

/** Cambios en cuarentena todavía sin resolver (en el dispositivo del dueño: los de todos). */
export function usePendingRejections(businessId: string): SyncRejectionRow[] | undefined {
  return useLiveQuery(
    () =>
      db.sync_rejections
        .where('status')
        .equals('pending')
        .filter((r) => r.business_id === businessId)
        .toArray()
        .then((rows) => rows.sort((a, b) => (a.op_created_at ?? a.rejected_at).localeCompare(b.op_created_at ?? b.rejected_at))),
    [businessId],
  );
}

/** Hace cuánto está esperando el cambio pendiente más viejo (ms), o null si no hay. */
export function useOldestPendingAge(): number | null | undefined {
  return useLiveQuery(async () => {
    const op = await oldestPendingOp(db);
    return op ? Date.now() - new Date(op.created_at).getTime() : null;
  });
}

const relative = new Intl.RelativeTimeFormat('es-AR', { numeric: 'auto' });

/** "hace 5 minutos", "hace 2 días". */
export function timeAgo(iso: string | number | null | undefined): string {
  if (iso === null || iso === undefined) return 'nunca';
  const ms = Date.now() - (typeof iso === 'number' ? iso : new Date(iso).getTime());
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return 'recién';
  if (minutes < 60) return relative.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 48) return relative.format(-hours, 'hour');
  return relative.format(-Math.round(hours / 24), 'day');
}

export const DAY_MS = 24 * 60 * 60_000;

export interface ConnectionTest {
  /** Función del servidor (ms), consultas a la base (ms cada una). null = falló. */
  server: number | null;
  queries: Array<number | null>;
  error: string | null;
  mode: 'app instalada' | 'navegador';
  network: string | null;
}

/** Mide cuánto tarda el servidor en responder desde este dispositivo (para diagnosticar lentitud). */
export async function testConnection(businessId: string): Promise<ConnectionTest> {
  let error: string | null = null;
  const time = async (fn: () => Promise<unknown>): Promise<number | null> => {
    const start = performance.now();
    try {
      await fn();
      return Math.round(performance.now() - start);
    } catch (e) {
      error ??= e instanceof Error ? e.message : String(e);
      return null;
    }
  };
  // Servicio de sesiones (el que se usa al entrar).
  const server = await time(async () => {
    const res = await fetch(`${env.supabaseUrl}/auth/v1/health`, { headers: { apikey: env.supabaseKey }, signal: AbortSignal.timeout(30_000) });
    if (!res.ok) throw new Error(`El servidor respondió ${res.status}`);
  });
  const queries: Array<number | null> = [];
  for (let i = 0; i < 3; i++) {
    queries.push(
      await time(async () => {
        const { error: e } = await supabase.rpc('sync_pull', { p_business_id: businessId, p_table: 'businesses', p_limit: 1 });
        if (e) throw new Error(e.message);
      }),
    );
  }
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
  return {
    server,
    queries,
    error,
    mode: window.matchMedia('(display-mode: standalone)').matches ? 'app instalada' : 'navegador',
    network: connection?.effectiveType ?? null,
  };
}
